'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { writeProgramCookie } from '@/lib/program/program-cookie';

/**
 * Decision 2 / AC6 — the anonymous auto-switch offer.
 *
 * An anonymous visitor who opens a shared link for a course outside their
 * resolved program still SEES the page (public marketing content; shared links
 * and SEO keep working). This banner offers to move them, with an explicit
 * dismiss.
 *
 * ⚠️ The cookie is NOT written on mount, deliberately. It has a 1-year max-age,
 * so one click on a shared Overseas link would silently move a US visitor's
 * catalog for a year, signalled only by a notice they have already scrolled
 * past, with no undo. (A Server Component also cannot call `cookies().set()` in
 * Next 15, so the write must be client-side regardless — mirroring how
 * `writeLocaleCookie` already works.)
 */
export function ProgramSwitchNotice({
  programSlug,
  programName,
}: {
  programSlug: string;
  programName: string;
}) {
  const t = useTranslations('public.courses.programNotice');
  const router = useRouter();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  function handleSwitch() {
    writeProgramCookie(programSlug);
    void queryClient.invalidateQueries();
    router.refresh();
  }

  return (
    <div
      role="status"
      data-testid="program-switch-notice"
      className="mb-6 flex flex-col gap-3 rounded-lg border border-[#2fa5fb] bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-[#1e306e]">{t('body', { programName })}</p>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={handleSwitch}
          className="rounded-md bg-[#1e306e] px-3 py-1.5 text-sm font-semibold text-white transition hover:opacity-90"
        >
          {t('browse', { programName })}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="rounded-md border border-[#1e306e] px-3 py-1.5 text-sm font-semibold text-[#1e306e] transition hover:bg-white"
        >
          {t('stay')}
        </button>
      </div>
    </div>
  );
}
